import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, CheckCircle, XCircle, Loader2, Navigation } from "lucide-react";
import { Button } from "../components/ui/button";
import { API_URL } from "../config/api";


export default function DriverLocationPage() {
  const { orderId, token } = useParams();
  const [status, setStatus] = useState("idle"); // idle, requesting, success, error
  const [errorMessage, setErrorMessage] = useState("");
  const [locationData, setLocationData] = useState(null);

  const requestLocation = async () => {
    setStatus("requesting");
    setErrorMessage("");

    if (!navigator.geolocation) {
      setStatus("error");
      setErrorMessage("Geolocation is not supported by your browser");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        setLocationData({ latitude, longitude, accuracy });

        try {
          const response = await fetch(
            `${API_URL}/track/${orderId}/${token}/submit?latitude=${latitude}&longitude=${longitude}&accuracy=${accuracy || 0}`,
            { 
              method: "POST",
              headers: {
                "Content-Type": "application/json"
              }
            }
          );

          if (response.ok) {
            setStatus("success");
          } else {
            const data = await response.json();
            setStatus("error");
            setErrorMessage(data.detail || "Failed to submit location");
          }
        } catch (error) {
          setStatus("error");
          setErrorMessage("Network error. Please try again.");
        }
      },
      (error) => {
        setStatus("error");
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setErrorMessage("Location permission denied. Please allow location access and try again.");
            break;
          case error.POSITION_UNAVAILABLE:
            setErrorMessage("Location unavailable. Please check your GPS settings.");
            break;
          case error.TIMEOUT:
            setErrorMessage("Location request timed out. Please try again.");
            break;
          default:
            setErrorMessage("Unable to get location. Please try again.");
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 30000,
        maximumAge: 0
      }
    );
  };

  // Auto-request on page load
  useEffect(() => {
    // Small delay to let the page render first
    const timer = setTimeout(() => {
      requestLocation();
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-900 to-blue-950 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center"
      >
        {/* Logo */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-blue-900">Europacific</h1>
          <p className="text-gray-500 text-sm">Logistics Tracking</p>
        </div>

        {/* Status Display */}
        {status === "idle" && (
          <div className="space-y-6">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
              <MapPin className="w-10 h-10 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Share Your Location</h2>
              <p className="text-gray-600">Tap the button below to share your current location with Europacific.</p>
            </div>
            <Button
              onClick={requestLocation}
              size="lg"
              className="w-full bg-blue-600 hover:bg-blue-700 text-lg py-6"
            >
              <Navigation className="w-5 h-5 mr-2" />
              Share Location
            </Button>
          </div>
        )}

        {status === "requesting" && (
          <div className="space-y-6">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
              <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Getting Location...</h2>
              <p className="text-gray-600">Please allow location access when prompted.</p>
            </div>
          </div>
        )}

        {status === "success" && (
          <div className="space-y-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-green-700 mb-2">Location Received!</h2>
              <p className="text-gray-600 mb-4">Thank you! Your location has been sent to Europacific.</p>
              {locationData && (
                <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-500">
                  <p>Lat: {locationData.latitude.toFixed(5)}</p>
                  <p>Lng: {locationData.longitude.toFixed(5)}</p>
                </div>
              )}
            </div>
            <p className="text-gray-500 text-sm">You can close this page now.</p>
          </div>
        )}

        {status === "error" && (
          <div className="space-y-6">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-red-700 mb-2">Error</h2>
              <p className="text-gray-600">{errorMessage}</p>
            </div>
            <Button
              onClick={requestLocation}
              size="lg"
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              Try Again
            </Button>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 pt-6 border-t text-xs text-gray-400">
          <p>Europacific Logistika d.o.o.</p>
          <p>+386 5 662 6380 | info@europacific.com</p>
        </div>
      </motion.div>
    </div>
  );
}
